import { useEffect, useState, useCallback } from 'react'

type Interlocutor = {
  firstName: string
  phone?: string | null
}

function isNetworkBad(): boolean {
  if (typeof navigator === 'undefined') return false

  if (!navigator.onLine) return true

  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection
  if (!connection) return false

  const { effectiveType, downlink, rtt } = connection
  if (effectiveType === 'slow-2g' || effectiveType === '2g') return true
  if (downlink !== undefined && downlink < 1) return true
  if (rtt !== undefined && rtt > 500) return true

  return false
}

type NetworkInformation = {
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g'
  downlink?: number
  rtt?: number
  addEventListener?: (type: string, listener: () => void) => void
  removeEventListener?: (type: string, listener: () => void) => void
}

export function useNetworkConnectionAlert(interlocutor: Interlocutor | null) {
  const [showAlert, setShowAlert] = useState(false)

  const dismissAlert = useCallback(() => {
    setShowAlert(false)
  }, [])

  useEffect(() => {
    if (!interlocutor) return

    const checkNetwork = () => {
      setShowAlert(isNetworkBad())
    }

    checkNetwork()

    const handleOffline = () => setShowAlert(true)
    const handleOnline = () => {
      setShowAlert(false)
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection
    if (connection?.addEventListener && connection?.removeEventListener) {
      connection.addEventListener('change', checkNetwork)
    }

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      if (connection?.removeEventListener) {
        connection.removeEventListener('change', checkNetwork)
      }
    }
  }, [interlocutor])

  return { showAlert, dismissAlert }
}
